import { useEffect, useState } from 'react';
import type React from 'react';

import { Spinner } from '@change/ds-core-ui';

type VideoState = 'paused' | 'buffering' | 'playing';

/**
 * This is a simplified set of HTML media states. See:
 * https://html.spec.whatwg.org/multipage/media.html#event-media-stalled
 */
const getVideoState = (eventType: string): VideoState => {
	if (eventType === 'playing') {
		return 'playing';
	}
	if (['waiting', 'stalled'].includes(eventType)) {
		return 'buffering';
	}
	return 'paused';
};

export const VideoStateIndicator: React.FC<{
	videoRef: React.RefObject<HTMLVideoElement | null>;
}> = ({ videoRef }) => {
	const [videoState, setVideoState] = useState<VideoState>('paused');
	const [hasControls, setHasControls] = useState(false);

	useEffect(() => {
		const videoEl = videoRef.current;
		if (!videoEl) {
			return () => {};
		}

		// Set initial controls state
		setHasControls(videoEl.controls);

		const handleEvent = (event: Event) => {
			setVideoState(getVideoState(event.type));
		};

		videoEl.addEventListener('playing', handleEvent);
		videoEl.addEventListener('pause', handleEvent);
		videoEl.addEventListener('waiting', handleEvent);
		videoEl.addEventListener('stalled', handleEvent);

		return () => {
			videoEl.removeEventListener('playing', handleEvent);
			videoEl.removeEventListener('pause', handleEvent);
			videoEl.removeEventListener('waiting', handleEvent);
			videoEl.removeEventListener('stalled', handleEvent);
		};
	}, [videoRef]);

	// If the video has controls then the custom indicator would just conflict
	const canBeVisible = !hasControls;
	const isVisible = canBeVisible && videoState === 'buffering';

	return (
		<div
			sx={{
				height: '20%',
				display: isVisible ? 'block' : 'none',
			}}
			data-testid="video-state-indicator"
		>
			<Spinner />
		</div>
	);
};
